import React, { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useUser, useClerk } from '@clerk/clerk-react'
import { useApp } from '../contexts/AppContext'

/**
 * Navigation Component
 *
 * Features:
 * - Responsive design with mobile hamburger menu
 * - User avatar/initials display for authenticated users
 * - Credit balance display with optimistic updates (desktop: next to name, mobile: next to hamburger)
 * - Sign Out functionality using latest Clerk 2025 patterns
 * - SortingCat branding
 * - Mobile-first Tailwind CSS approach
 */

const Navigation: React.FC = () => {
  const { user, isSignedIn, isLoaded } = useUser()
  const { openSignUp, signOut } = useClerk()
  const { displayedCreditBalance, isCreditsLoading } = useApp()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Helper function to get user initials
  const getUserInitials = () => {
    if (!user) return 'U'
    const firstName = user.firstName || ''
    const lastName = user.lastName || ''
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || user.emailAddresses[0]?.emailAddress.charAt(0).toUpperCase() || 'U'
  }

  // Helper function to get user display name
  const getUserDisplayName = () => {
    if (!user) return ''
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.emailAddresses[0]?.emailAddress || ''
  }

  const handleSignOut = async () => {
    try {
      await signOut({ redirectUrl: '/' })
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  return (
    <nav className="bg-gradient-to-r from-orange-50 to-orange-100 shadow-sm border-b border-orange-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Brand */}
          <div className="flex items-center">
            <a href="/" className="flex items-center space-x-2">
              <img 
                src="/sorting_cat_mascot.png" 
                alt="SortingCat" 
                className="h-8 w-8 object-cover rounded-full"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent">
                SortingCat
              </span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="flex items-center space-x-6">
              {/* Navigation Links */}
              
              {/* Authentication */}
              {isLoaded && (
                <div className="flex items-center space-x-3">
                  {isSignedIn ? (
                    <>
                      {/* Credits Display - Desktop */}
                      <div className="flex items-center space-x-2 bg-orange-100 rounded-full px-3 py-1.5 border border-orange-300">
                        <span className="text-xs font-semibold text-purple-700">Credits</span>
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span className="text-sm font-bold text-gray-900">
                          {isCreditsLoading ? '...' : displayedCreditBalance.toLocaleString()}
                        </span>
                      </div>

                      {/* User Avatar with Fallback to Initials - Clickable link to account */}
                      <Link
                        to="/account"
                        className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer"
                      >
                        {user?.imageUrl ? (
                          <img
                            src={user.imageUrl}
                            alt={getUserDisplayName()}
                            className="w-8 h-8 rounded-full border-2 border-purple-200"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-medium">
                            {getUserInitials()}
                          </div>
                        )}
                        <span className="text-purple-700 text-sm font-medium hidden lg:block">
                          {getUserDisplayName()}
                        </span>
                      </Link>

                      <button
                        onClick={handleSignOut}
                        className="bg-red-100 text-red-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-red-200 transition-colors"
                      >
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => openSignUp()}
                      className="bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-purple-700 transition-colors"
                    >
                      Sign In/Up
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button and credits */}
          <div className="md:hidden flex items-center space-x-3">
            {/* Credits Display - Mobile */}
            {isSignedIn && isLoaded && (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-sm font-bold text-gray-900">
                  {isCreditsLoading ? '...' : displayedCreditBalance.toLocaleString()}
                </span>
              </div>
            )}

            {/* Hamburger Menu */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-purple-700 hover:text-purple-900 hover:bg-orange-200 transition-colors"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {/* Hamburger icon */}
              <svg
                className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              {/* Close icon */}
              <svg
                className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:hidden`}>
        <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-orange-300">
          {/* Mobile Navigation Links */}

          {/* Mobile Authentication */}
          {isLoaded && (
            <div className="pt-2 border-t border-orange-200">
              {isSignedIn ? (
                <div className="space-y-1">
                  {/* Mobile User Info - Clickable link to account */}
                  <Link
                    to="/account"
                    className="flex items-center space-x-3 px-3 py-2 hover:bg-orange-50 rounded-md transition-colors cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {user?.imageUrl ? (
                      <img
                        src={user.imageUrl}
                        alt={getUserDisplayName()}
                        className="w-10 h-10 rounded-full border-2 border-purple-200"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center text-base font-medium">
                        {getUserInitials()}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="text-purple-700 font-medium text-base">
                        {getUserDisplayName()}
                      </div>
                      <div className="text-purple-500 text-sm">
                        {user?.emailAddresses[0]?.emailAddress}
                      </div>
                    </div>
                  </Link>

                  <Link
                    to="/account"
                    className="text-purple-700 hover:text-purple-900 hover:bg-orange-100 block px-3 py-2 rounded-md text-base font-medium transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Account
                  </Link>

                  <button
                    onClick={() => {
                      handleSignOut()
                      setIsMobileMenuOpen(false)
                    }}
                    className="w-full text-left bg-red-100 text-red-700 block px-3 py-2 rounded-md text-base font-medium hover:bg-red-200 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    openSignUp()
                    setIsMobileMenuOpen(false)
                  }}
                  className="w-full text-left bg-purple-600 text-white block px-3 py-2 rounded-md text-base font-medium hover:bg-purple-700 transition-colors"
                >
                  Sign In/Up
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  )
}

export default Navigation